import { h, render, Component, createRef } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';
import Barcode from 'react-barcode';

import localUtil from '../../lib/util';
import { journalHasUserContent } from '../../lib/journalSaveDiff';
import { printViaIframe } from '../../lib/printHelper';
import fields from '../../lib/fields';
import { addPrescriptionProductLines } from '../../lib/prescriptionAutoProduct';
import { canLookup as canLookupNrr, buildRows as buildNrrRows, changedFields as nrrChangedFields } from '../../lib/nrrAnimal';
import { stageSaleAnimalChipId } from '../../lib/saleAnimalChipId';

import ViewFields from '../../components/form/viewFields';
import Input from '../../components/form/input';
import DyrejournalSaleSearch from '../../components/dyrejournal/search';
import DyrejournalJournalListMini from '../../components/dyrejournal/listJournalMini';
import LineGraphMini from '../../components/lineGraph/mini';

import definitionNo from '../../languages/no.json';
import definitionEn from '../../languages/en.json';
import { animalAge } from '../../lib/animalAge';

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionEn,
};

function sortByDateAsc(a, b) {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateA - dateB;
}

/**
 * Map DyreID gender values to local gender format
 * DyreID uses: "Hanndyr", "Hunnkjønn", "Kastrert hanndyr", etc.
 * Local uses: "Hann", "Hunn", "Kastrert hann", "Kastrert hunn"
 */
function mapDyreIdGender(dyreIdGender) {
    if (!dyreIdGender) {
        return dyreIdGender;
    }

    const genderLower = dyreIdGender.toLowerCase().trim();

    // Castrated male
    if (genderLower.includes('kastrert') && (genderLower.includes('hann') || genderLower.includes('male'))) {
        return 'Kastrert hann';
    }

    // Castrated/sterilized female
    if ((genderLower.includes('kastrert') || genderLower.includes('sterilisert')) &&
        (genderLower.includes('hunn') || genderLower.includes('female'))) {
        return 'Kastrert hunn';
    }

    // Male
    if (genderLower.includes('hann') || genderLower.includes('male') || genderLower === 'm') {
        return 'Hann';
    }

    // Female
    if (genderLower.includes('hunn') || genderLower.includes('female') || genderLower === 'f') {
        return 'Hunn';
    }

    // Return original if no match
    return dyreIdGender;
}

@withText(props => ({
    newJournalAreYouSure: <Text id='dyrejournal.new-journal-are-you-sure'>Are you sure you want to create a new journal?</Text>,
}))
@observer
class AnimalListMini extends Component {
	constructor(props) {
        super(props);
        this.state = {
            smsSending: {},
            smsSent: {},
            checkingWithDyreId: {},
            sendingToDyreId: {},
            sentToDyreId: {},
            dyreIdResponse: {},
            checkingWithNrr: {},
            nrrResponse: {},
            updatingAllNrr: {},
            updatingAnimal: {},
            updatedAnimal: {},
            saveErrorAnimal: {},
            lastWeight: {},
            weights: {},
            creatingJournal: false,
            showNewJournalConfirm: false,
            newJournalAnimalId: null,
        };

        this.editChipIdTimer = null;
    }

    onClickViewAnimal = e => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('.animal-line').dataset;
        const { drawerLevel = 1 } = this.props;
		const { appState, saleStore, animalStore } = this.props.stores;
		const { drawerHeightLarge } = appState;

		appState.openDrawer('viewAnimal', {
			height: drawerHeightLarge,
            isNew: false,
            hideAdmin: true,
            id: parseInt(id, 10),
            callback: () => {
                // console.log('onClickViewAnimal callback');
                const { animal } = animalStore;
                // A deleted or out-of-scope animal id loads as an empty result,
                // leaving animalStore.animal undefined — nothing to sync back.
                if (!animal || !animal.id) {
                    return;
                }
                const { newSale, visitorAnimals } = saleStore;
                const { animals } = newSale;
                const idx = animals.findIndex(a => a.id === animal.id);
                if (idx > -1) {
                    saleStore.updateObjectKeyArrValue('newSale', 'animals', idx, animal);
                }
                const idx2 = visitorAnimals.findIndex(a => a.id === animal.id);
                if (idx2 > -1) {
                    saleStore.updateKeyArrValue('visitorAnimals', idx2, animal);
                }
            },
		}, drawerLevel + 1);
    }

    onClickEditAnimal = e => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('.animal-line').dataset;
        const { drawerLevel = 1 } = this.props;
		const { appState, saleStore, animalStore } = this.props.stores;
		const { drawerHeightLarge } = appState;

		appState.openDrawer('editAnimal', {
			height: drawerHeightLarge,
            isNew: false,
            hideAdmin: true,
            id: parseInt(id, 10),
            callback: () => {
                // console.log('onClickEditAnimal callback');
                const { animal } = animalStore;
                // A deleted or out-of-scope animal id loads as an empty result,
                // leaving animalStore.animal undefined — nothing to sync back.
                if (!animal || !animal.id) {
                    return;
                }
                const { newSale, visitorAnimals } = saleStore;
                const { animals } = newSale;
                const idx = animals.findIndex(a => a.id === animal.id);
                if (idx > -1) {
                    saleStore.updateObjectKeyArrValue('newSale', 'animals', idx, animal);
                }
                const idx2 = visitorAnimals.findIndex(a => a.id === animal.id);
                if (idx2 > -1) {
                    saleStore.updateKeyArrValue('visitorAnimals', idx2, animal);
                }
            },
		}, drawerLevel + 1);
    }

    onClickSetSelectedAnimal = e => {
        e.stopPropagation();
        e.preventDefault();
        const { animal } = e.target.closest('button').dataset;
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;
        const { selectedAnimal } = newSale;
        const animalId = parseInt(animal, 10);
        // Single-select control: clicking the active animal clears it, otherwise it
        // becomes the only target (resets any multi-select made in the journal table).
        if (animalId === selectedAnimal) {
            saleStore.setSelectedTargetAnimal(null);
        } else {
            saleStore.setSelectedTargetAnimal(animalId);
        }
    }

    onClickRemoveAnimal = e => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('div').dataset;
        const animalId = parseInt(id, 10);
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;
        const { animals, journals = [] } = newSale;
        const newAnimals = animals.filter(v => v.id !== animalId);
        // Also remove journals that belong to this animal
        const newJournals = journals.filter(j => j.animal !== animalId);
        saleStore.updateObjectKeyValue('newSale', 'animals', newAnimals);
        saleStore.updateObjectKeyValue('newSale', 'journals', newJournals);
    }

    saveJournal = async e => {
        const { journalStore } = this.props.stores;
        const { newJournal } = journalStore;

        if (newJournal.id) {
            // Diff save: only send fields changed since the journal drawer loaded this
            // journal, so a stale copy can't overwrite text another user/window saved
            // in the meantime (TICKET-01303).
            await journalStore.saveJournalDiff({ ...newJournal });
        } else if (journalHasUserContent(newJournal)) {
            const response = await journalStore.insert(newJournal);
            // Update current newJournal object
            const journal = response?.data?.journal;
            journalStore.updateObjectKeyValue('newJournal', 'id', journal?.id);
            journalStore.updateObjectKeyValue('newJournal', 'journalNumber', journal?.journalNumber);
            journalStore.setNewJournalBaseline(journalStore.newJournal);
        }
        // TICKET-01394: a drawer-stamped newJournal with no id and nothing typed is NOT
        // inserted — closing the new-journal drawer without writing anything used to
        // POST it here (via the drawer-close callback), leaving empty journals on the
        // sale next to the vet's real journal for the same animal.
        return true;
    }

    onClickViewJournals = e => {
        e.stopPropagation();
        e.preventDefault();
        const { animal } = e.target.closest('button').dataset;
        const { drawerLevel = 1 } = this.props;
		const { appState } = this.props.stores;
		const { drawerHeightLarge } = appState;

		appState.openDrawer('viewAnimalJournals', {
			height: drawerHeightLarge,
            id: parseInt(animal, 10),
		}, drawerLevel + 1);
    }

    onClickViewPrescriptions = e => {
        e.stopPropagation();
        e.preventDefault();
        const { animal } = e.target.closest('button').dataset;
        const { drawerLevel = 1 } = this.props;
		const { appState } = this.props.stores;
		const { drawerHeightLarge } = appState;

		appState.openDrawer('viewAnimalPrescriptions', {
			height: drawerHeightLarge,
            id: parseInt(animal, 10),
		}, drawerLevel + 1);
    }

    onClickViewIdexxResults = e => {
        e.stopPropagation();
        e.preventDefault();
        const { animal } = e.target.closest('button').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightLarge } = appState;

        appState.openDrawer('viewAnimalIdexxResults', {
            height: drawerHeightLarge,
            id: parseInt(animal, 10),
        }, drawerLevel + 1);
    }

    onClickNewJournal = e => {
        e.stopPropagation();
        e.preventDefault();

        // Prevent double-clicks while creating journal
        if (this.state.creatingJournal) {
            return;
        }

        // Open a prominent, centered in-app confirmation modal instead of the native
        // browser confirm() that pops up as an easy-to-miss little bar at the top of the
        // page (TICKET-01156). The real create logic runs in confirmNewJournal().
        const { animal } = e.target.closest('button').dataset;
        this.setState({
            showNewJournalConfirm: true,
            newJournalAnimalId: parseInt(animal, 10),
        });
    }

    cancelNewJournal = () => {
        this.setState({ showNewJournalConfirm: false });
    }

    confirmNewJournal = async () => {
        const {
            animals = [],
        } = this.props;

        if (this.state.creatingJournal) {
            return;
        }
        const { newJournalAnimalId: animalIdInt } = this.state;
        this.setState({ showNewJournalConfirm: false });

        const {
            drawerLevel = 1,
            saveSale = () => {},
            saleIsLocked = false,
        } = this.props;
		const { appState, saleStore, journalStore, userStore } = this.props.stores;
        const { newSale } = saleStore;
        const { customer } = userStore;
        // Clinics with autoCreateJournal on get the journal created + saved right away
        // (like the Ny resept flow) so it has an id and autosaves from the first
        // keystroke; otherwise the classic blank drawer opens and the vet presses
        // "Opprett journal" to create it.
        const autoCreate = parseInt(customer?.featureFlags?.autoCreateJournal, 10) === 1;

        this.setState({ creatingJournal: true });
        try {
            if (autoCreate) {
                await this.createJournalAndAutoSave({ animalIdInt, drawerLevel, saveSale, saleIsLocked });
                return;
            }
            const isSaved = await saveSale({ calledFrom: 'listAnimalMini.onClickNewJournal' });
            appState.openDrawerRight('newJournal', {
                isNew: true,
                hideAdmin: true,
                saveSale,
                saleIsLocked,
                calendarEventId: newSale.calendarEvent,
                animals: animals.filter(a => a.id === animalIdInt),
                callback: async (nj, keepOpen) => {
                    const { newJournal } = journalStore;
                    // console.log('onClickNewJournal callback', newJournal);
                    if (!keepOpen) {
                        appState.toggleDrawerRight(false, drawerLevel + 1);
                    }
                    if (newJournal) {
                        if (!newJournal.id) {
                            await this.saveJournal();
                        }
                        // TICKET-01394: only attach a journal that actually exists. The
                        // insert above is skipped for content-less journals (drawer
                        // closed without typing anything) and can also fail — an id-less
                        // object must never be pushed into newSale.journals.
                        if (newJournal.id) {
                            const { newSale } = saleStore;
                            const { journals } = newSale;
                            const isFound = journals.find(a => a.id === newJournal.id);
                            const idx = journals.findIndex(a => a.id === newJournal.id);
                            if (idx > -1) {
                                saleStore.updateObjectKeyArrValue('newSale', 'journals', idx, newJournal);
                                await saveSale({ calledFrom: 'listAnimalMini.onClickNewJournal.callback' });
                            } else {
                                saleStore.updateObjectKeyValue('newSale', 'journals', [...journals, newJournal]);
                                await saveSale({ calledFrom: 'listAnimalMini.onClickNewJournal.callback' });
                            }
                        }
                        // setTimeout(() => {
                        //     this.loadAll(this.props, false);
                        // }, 1000);
                    }
                },
            }, drawerLevel + 1);
        } finally {
            this.setState({ creatingJournal: false });
        }
    }

    // Builds the anamnesis (chiefComplaint) prefill from the booking a sale came from,
    // so the text the clinic wrote in the timebestilling follows into the journal.
    // Returns an empty string when the sale has no booking or the booking has no title.
    getBookingChiefComplaint = async calendarEventId => {
        if (!calendarEventId) {
            return '';
        }
        const { calendarEventStore } = this.props.stores;
        const calendarEvents = await calendarEventStore.load(calendarEventId, false, { skipUpdate: true });
        const calendarEvent = calendarEvents?.[0];
        if (!calendarEvent || !calendarEvent.title) {
            return '';
        }
        return `${calendarEvent.title} ${calendarEvent.body || ''}`.trim();
    }

    // Auto-create flow for the animal-mini "Ny journal" button, gated by the clinic's
    // featureFlags.autoCreateJournal. Creates + saves the journal immediately (so it has
    // an id and autosaves as the vet types), attaches it to the sale, then opens it in
    // the editJournal drawer. Mirrors onClickNewPrescription's create path. The insert is
    // covered by the journalStore double-submit guard (TICKET-01239), and confirmNewJournal
    // already blocks re-entry via state.creatingJournal, so this cannot create duplicates.
    createJournalAndAutoSave = async ({ animalIdInt, drawerLevel = 1, saveSale = () => {}, saleIsLocked = false }) => {
        const { appState, saleStore, journalStore, userStore } = this.props.stores;
        const { user } = userStore;
        const { newSale } = saleStore;
        const { visitors = [] } = newSale;

        // Save the sale first so it has an id to attach the journal to.
        await saveSale({ calledFrom: 'listAnimalMini.onClickNewJournal.autoCreate' });

        journalStore.createNewJournal();
        journalStore.updateObjectKeyValue('newJournal', 'animal', animalIdInt);
        if (visitors[0]?.id) {
            journalStore.updateObjectKeyValue('newJournal', 'visitor', visitors[0].id);
        }
        if (saleStore.newSale.id) {
            journalStore.updateObjectKeyValue('newJournal', 'sale', saleStore.newSale.id);
        }
        journalStore.updateObjectKeyValue('newJournal', 'veterinary', user.id);
        // Inherit the visit date when the saleDateTop feature is on (same as Ny resept).
        const journalDate = (saleStore.saleDateTopEnabled && newSale.saleDate) ? newSale.saleDate : new Date();
        journalStore.updateObjectKeyValue('newJournal', 'date', journalDate);
        // Carry the booking text into the anamnesis, exactly like the classic
        // "Opprett journal" drawer does (newJournal.js loadJournal). This path used to
        // drop it, so clinics with featureFlags.autoCreateJournal on silently lost the
        // timebestilling text in every journal. (TICKET-01403)
        const bookingChiefComplaint = await this.getBookingChiefComplaint(saleStore.newSale?.calendarEvent);
        if (bookingChiefComplaint) {
            journalStore.updateObjectKeyValue('newJournal', 'chiefComplaint', bookingChiefComplaint);
        }
        // Same prefill as newJournal.js loadJournal: the owner's online prescription request
        // (kept in the sale description) becomes the anamnesis (TICKET-01849).
        if (saleStore.newSale.prescriptionOrder > 0 && saleStore.newSale.description) {
            const { chiefComplaint } = journalStore.newJournal;
            journalStore.updateObjectKeyValue('newJournal', 'chiefComplaint', `${chiefComplaint ? `${chiefComplaint}\n` : ''}${saleStore.newSale.description}`);
        }

        const response = await journalStore.insert(journalStore.newJournal);
        const inserted = response?.data?.journal;
        if (!inserted?.id) {
            appState.addToast({
                type: 'error',
                message: 'Kunne ikke opprette journal. Prøv igjen.',
                icon: 'triangle-exclamation',
            }, 8000);
            return;
        }
        journalStore.updateObjectKeyValue('newJournal', 'id', inserted.id);
        journalStore.updateObjectKeyValue('newJournal', 'journalNumber', inserted.journalNumber);
        appState.trackJournal('create', inserted);

        // Attach the fresh journal to the sale.
        const { journals = [] } = saleStore.newSale;
        const savedJournal = { ...journalStore.newJournal };
        saleStore.updateObjectKeyValue('newSale', 'journals', [...journals, savedJournal]);
        await saveSale({ calledFrom: 'listAnimalMini.onClickNewJournal.autoCreate.afterJournal' });

        // Open the just-created journal in edit mode (by id) so it autosaves. We open
        // editJournal rather than re-opening newJournal, because the newJournal drawer
        // resets journalStore.newJournal on mount and would wipe this record.
        appState.openDrawerRight('editJournal', {
            isNew: false,
            hideAdmin: true,
            saveSale,
            saleIsLocked,
            id: inserted.id,
            callback: async () => {
                const { newJournal } = journalStore;
                const { newSale: ns } = saleStore;
                const { journals: js = [] } = ns;
                const idx = js.findIndex(a => a.id === newJournal.id);
                if (idx > -1) {
                    saleStore.updateObjectKeyArrValue('newSale', 'journals', idx, newJournal);
                } else {
                    saleStore.updateObjectKeyValue('newSale', 'journals', [...js, newJournal]);
                }
                await saveSale({ calledFrom: 'listAnimalMini.onClickNewJournal.autoCreate.callback' });
            },
        }, drawerLevel + 1);
    }

    onClickNewPrescription = async e => {
        e.stopPropagation();
        e.preventDefault();

        // Prevent double-clicks while creating journal
        if (this.state.creatingJournal) {
            return;
        }

        const { animal } = e.target.closest('button').dataset;
        const animalIdInt = parseInt(animal, 10);

        const {
            drawerLevel = 1,
            saveSale = () => {},
            saleIsLocked = false,
            animals = [],
        } = this.props;
        const { appState, saleStore, journalStore, userStore, productStore } = this.props.stores;
        const { drawerHeightLarge } = appState;
        const { user } = userStore;
        const { newSale } = saleStore;
        const { visitors = [] } = newSale;

        this.setState({ creatingJournal: true });
        try {
            // 1. Save sale first so it has an id
            await saveSale({ calledFrom: 'listAnimalMini.onClickNewPrescription' });

            // 2. Initialize newJournal in the store
            journalStore.createNewJournal();

            // 3. Populate minimal journal fields
            journalStore.updateObjectKeyValue('newJournal', 'animal', animalIdInt);
            if (visitors[0]?.id) {
                journalStore.updateObjectKeyValue('newJournal', 'visitor', visitors[0].id);
            }
            if (saleStore.newSale.id) {
                journalStore.updateObjectKeyValue('newJournal', 'sale', saleStore.newSale.id);
            }
            journalStore.updateObjectKeyValue('newJournal', 'veterinary', user.id);
            // When the saleDateTop feature is on, inherit the sale's visit date.
            const prescriptionJournalDate = (saleStore.saleDateTopEnabled && newSale.saleDate) ? newSale.saleDate : new Date();
            journalStore.updateObjectKeyValue('newJournal', 'date', prescriptionJournalDate);
            journalStore.updateObjectKeyValue('newJournal', 'chiefComplaint', 'Ny resept');

            // 4. Insert journal and capture id + journalNumber
            const { newJournal } = journalStore;
            const response = await journalStore.insert(newJournal);
            const inserted = response?.data?.journal;
            if (!inserted?.id) {
                appState.addToast({
                    type: 'error',
                    message: 'Kunne ikke opprette journal. Prøv igjen.',
                    icon: 'triangle-exclamation',
                }, 8000);
                return;
            }
            journalStore.updateObjectKeyValue('newJournal', 'id', inserted.id);
            journalStore.updateObjectKeyValue('newJournal', 'journalNumber', inserted.journalNumber);
            appState.trackJournal('create', inserted);

            // 5. Add journal to newSale.journals and save sale
            const { journals = [] } = saleStore.newSale;
            const savedJournal = { ...journalStore.newJournal };
            saleStore.updateObjectKeyValue('newSale', 'journals', [...journals, savedJournal]);
            await saveSale({ calledFrom: 'listAnimalMini.onClickNewPrescription.afterJournal' });

            // 6. Open prescription drawer — pass journal/animal/sale IDs explicitly
            // so the prescription is reliably linked to the journal we just created,
            // regardless of any intervening state resets on journalStore.newJournal.
            appState.openDrawer('addPrescription', {
                height: drawerHeightLarge,
                isNew: true,
                hideAdmin: true,
                saleIsLocked,
                journalId: inserted.id,
                animalId: animalIdInt,
                saleId: saleStore.newSale.id,
                callback: async prescription => {
                    appState.toggleDrawer(false, drawerLevel + 1);
                    if (prescription) {
                        const { newJournal: nj } = journalStore;
                        const { prescriptions = [] } = nj;
                        const isFound = prescriptions.find(a => a.id === prescription.id);
                        if (!isFound) {
                            journalStore.updateObjectKeyValue(
                                'newJournal',
                                'prescriptions',
                                [...prescriptions, prescription],
                            );
                        }
                    }
                    if (prescription) {
                        // Feature: auto-add the clinic's prescription fee product once per sale.
                        await addPrescriptionProductLines({
                            saleStore,
                            productStore,
                            userStore,
                            animalId: animalIdInt,
                        });
                    }
                    // Persist updated sale (journal with prescriptions)
                    const { newSale: ns } = saleStore;
                    const { journals: js = [] } = ns;
                    const idx = js.findIndex(j => j.id === journalStore.newJournal.id);
                    if (idx > -1) {
                        saleStore.updateObjectKeyArrValue('newSale', 'journals', idx, journalStore.newJournal);
                    }
                    await saveSale({ calledFrom: 'listAnimalMini.onClickNewPrescription.prescriptionCallback' });
                },
            }, drawerLevel + 1);
        } finally {
            this.setState({ creatingJournal: false });
        }
    }

    onClickNewNotification = async e => {
        e.stopPropagation();
        e.preventDefault();
        const {
            drawerLevel = 1,
            animals = [],
        } = this.props;
		const { appState } = this.props.stores;
        const { drawerHeightMedium } = appState;

        const { animal } = e.target.closest('button').dataset;
        const animalIdInt = parseInt(animal, 10);
        const animalObject = animals.find(a => a.id === animalIdInt);
        if (!animalObject) {
            return;
        }

        appState.openDrawer('newNotificationQuick', {
			height: drawerHeightMedium,
            animal: { id: animalObject.id, name: animalObject.name },
		}, drawerLevel + 1);
    }

    onClickNewAdmission = async e => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('button').dataset;

        const { animalStore, saleStore, appState } = this.props.stores;

        const { animals } = this.props;;
        const animal = animals.find(a => a.id === parseInt(id, 10));

        // updateObjectFieldByName({ objectName, namePlural = this.namePlural, id, field, value, findBy = '
        if (!animal) {
            return;
        }
        const response = await animalStore.save({
            isAdmittedToClinic: animal.isAdmittedToClinic ? 'false' : 'true',
        }, parseInt(id, 10));

        if (response && response.status < 300) {
            saleStore.updateObjectFieldByName({
                objectName: 'newSale',
                namePlural: 'animals',
                id: parseInt(id, 10),
                field: 'isAdmittedToClinic',
                value: animal.isAdmittedToClinic ? false : true,
            });
        } else {
            appState.addToast({
                type: 'error',
                message: 'Kunne ikke oppdatere innleggelse. Sjekk internettforbindelsen og prøv igjen.',
                icon: 'triangle-exclamation',
            }, 8000);
        }
    }

    onClickNewBooking = async e => {
        e.stopPropagation();
        e.preventDefault();
        const {
            drawerLevel = 1,
            saveSale = () => {},
            saleIsLocked = false,
            animals = [],
        } = this.props;
		const { appState, saleStore, calendarStore } = this.props.stores;
        const { drawerHeightLarge } = appState;
        const { newSale } = saleStore;

        const { animal } = e.target.closest('button').dataset;
        const animalIdInt = parseInt(animal, 10);

        appState.openDrawer('newCalendarEvent', {
			height: drawerHeightLarge,
            isNew: true,
            hideAdmin: true,
            calendarType: 'booking',
            animals: animals.filter(a => a.id === animalIdInt),
            inputDate: newSale.saleDate,
            saleIsLocked,
            callback: async booking => {
                // console.log('newCalendarEvent callback', booking);
                appState.toggleDrawer(false, drawerLevel + 1);
                calendarStore.resetDragging();
                this.loadAnimalBookings();
            },
		}, drawerLevel + 1);
    }

    onClickSendBookingSms = async e => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('button').dataset;
        const { calendarEventStore } = this.props.stores;
        // console.log('onClickSendBookingSms', id);
        const { smsSent, smsSending } = this.state;
        smsSending[id] = true;
        this.setState({ smsSending });

        await calendarEventStore.sendSMS(id);

        smsSending[id] = false;
        smsSent[id] = true;
        this.setState({ smsSent, smsSending });
    }

    onClickViewBooking = e => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('.calendar-event').dataset;

        const { appState, calendarStore } = this.props.stores;
        const { eventId } = calendarStore
        if (eventId) {
            return;
        }

        const { drawerLevel = 1, callback = () => {} } = this.props;
        const { drawerHeightMedium } = appState;

        appState.openDrawer('viewCalendarEvent', {
            height: drawerHeightMedium,
            id: parseInt(id, 10),
            showEdit: true,
            callback: event => {
                callback(event);
                appState.toggleDrawer(false, drawerLevel + 2);
                calendarStore.resetDragging();
            },
        }, drawerLevel + 2);
    }

    onClickFindFiles = e => {
        e.stopPropagation();
        e.preventDefault();
        const { animal } = e.target.closest('button').dataset;
        const { appState } = this.props.stores;
        const { drawerLevel = 1, callback = () => {} } = this.props;
        const { drawerHeightMedium } = appState;

        appState.openDrawer('listFileTable', {
            animalId: animal,
            height: drawerHeightMedium,
            callback: event => {},
        }, drawerLevel + 1);
    }

    onClickAiSummary = e => {
        e.stopPropagation();
        e.preventDefault();
        const { animal } = e.target.closest('button').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightLarge } = appState;

        appState.openDrawer('aiAnimalSummary', {
            height: drawerHeightLarge,
            animalId: parseInt(animal, 10),
        }, drawerLevel + 1);
    }

    onClickSendToDyreId = async e => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('button').dataset;
        const { animalStore } = this.props.stores;
        const { sendingToDyreId = {} } = this.state;

        sendingToDyreId[id] = true;
        this.setState({ sendingToDyreId });

        await animalStore.dyreIdRegisterPetOwnerInfoV1(parseInt(id, 10));
        sendingToDyreId[id] = false;
        this.setState({ sendingToDyreId });

        const { sentToDyreId } = this.state;
        sentToDyreId[id] = true;
        this.setState({ sentToDyreId });
    }

    onClickCheckChipId = async e => {
        e.stopPropagation();
        e.preventDefault();
        const target = e.currentTarget || e.target.closest('button') || e.target.closest('i');
        const { chipid, animal } = target.dataset;
        console.log('onClickCheckChipId', { chipid, animal });
        const { animalStore, saleStore } = this.props.stores;
        const { checkingWithDyreId = {} } = this.state;

        checkingWithDyreId[chipid] = true;
        this.setState({ checkingWithDyreId });

        const response = await animalStore.dyreIdGetChipInfo(chipid);
        // console.log({ response: response.data });
        const { dyreIdResponse } = this.state;
        dyreIdResponse[chipid] = response?.data;
        this.setState({ dyreIdResponse });
        checkingWithDyreId[chipid] = false;
        this.setState({ checkingWithDyreId });

        // Sync DyreID lost status back to animal record
        const animalId = parseInt(animal, 10);
        const dyreIdInfo = response?.data;
        if (animalId && dyreIdInfo && (dyreIdInfo.IsSuccess === 'true' || dyreIdInfo.IsSuccess === true)) {
            const remoteIsLost = dyreIdInfo.IsLost === 'true' || dyreIdInfo.IsLost === true;
            const { newSale = {}, visitorAnimals = [] } = saleStore;
            const localAnimal = (newSale.animals || []).find(a => a.id === animalId)
                || visitorAnimals.find(a => a.id === animalId);
            const localIsLost = !!localAnimal?.isLost;
            if (localAnimal && remoteIsLost !== localIsLost) {
                await animalStore.save({
                    id: animalId,
                    isLost: remoteIsLost,
                    isLostDate: remoteIsLost ? new Date().toISOString() : null,
                });
                const idx = (newSale.animals || []).findIndex(a => a.id === animalId);
                if (idx > -1) {
                    saleStore.updateObjectKeyArrFieldValue('newSale', 'animals', idx, 'isLost', remoteIsLost);
                }
                const idx2 = visitorAnimals.findIndex(a => a.id === animalId);
                if (idx2 > -1) {
                    saleStore.updateArrayKeyValue('visitorAnimals', idx2, 'isLost', remoteIsLost);
                }
            }
        }
    }

    closeDyreIdInfo = e => {
        e.stopPropagation();
        e.preventDefault();
        const { chipid } = e.target.closest('i').dataset;
        const { dyreIdResponse } = this.state;
        delete dyreIdResponse[chipid];
        this.setState({ dyreIdResponse });
    }

    onClickCheckNrr = async e => {
        e.stopPropagation();
        e.preventDefault();
        const target = e.currentTarget || e.target.closest('button');
        const { chipid } = target.dataset;
        const { animalStore } = this.props.stores;
        const { checkingWithNrr } = this.state;
        checkingWithNrr[chipid] = true;
        this.setState({ checkingWithNrr });

        const response = await animalStore.nrrGetCat(chipid);
        const { nrrResponse } = this.state;
        nrrResponse[chipid] = response || { status: 504, message: 'NRR did not respond.' };
        checkingWithNrr[chipid] = false;
        this.setState({ nrrResponse, checkingWithNrr });
    }

    closeNrrInfo = e => {
        e.stopPropagation();
        e.preventDefault();
        const { chipid } = e.target.closest('i').dataset;
        const { nrrResponse } = this.state;
        delete nrrResponse[chipid];
        this.setState({ nrrResponse });
    }

    // "Oppdater alle": one save with every differing NRR field, then patch the in-memory
    // sale animals field by field exactly like updateAnimal does.
    updateAnimalFromNrr = async e => {
        e.stopPropagation();
        e.preventDefault();
        const { id, chipid, index } = e.target.closest('button').dataset;
        const animalId = parseInt(id, 10);
        const { animalStore, saleStore, appState } = this.props.stores;
        const { nrrResponse, updatingAllNrr, updatingAnimal, updatedAnimal, saveErrorAnimal } = this.state;
        const cat = nrrResponse[chipid]?.data?.cats?.[parseInt(index, 10)];
        const animal = (animalStore.animals || []).find(a => a.id === animalId)
            || (animalStore.currentAnimals || []).find(a => a.id === animalId)
            || (saleStore.newSale.animals || []).find(a => a.id === animalId);
        const changes = nrrChangedFields(animal, cat);
        const fieldNames = Object.keys(changes);
        if (fieldNames.length === 0) {
            return;
        }
        updatingAllNrr[animalId] = true;
        if (!updatingAnimal[animalId]) {
            updatingAnimal[animalId] = {};
        }
        // Clear any previous error for these fields
        if (!saveErrorAnimal[animalId]) {
            saveErrorAnimal[animalId] = {};
        }
        fieldNames.forEach(field => {
            updatingAnimal[animalId][field] = true;
            saveErrorAnimal[animalId][field] = false;
        });
        this.setState({ updatingAllNrr, updatingAnimal, saveErrorAnimal });

        const response = await animalStore.save({ id: animalId, ...changes });

        fieldNames.forEach(field => { updatingAnimal[animalId][field] = false; });
        updatingAllNrr[animalId] = false;
        if (response && response.status < 300) {
            const { newSale, visitorAnimals } = saleStore;
            const idx = (newSale.animals || []).findIndex(a => a.id === animalId);
            const idx2 = (visitorAnimals || []).findIndex(a => a.id === animalId);
            if (!updatedAnimal[animalId]) {
                updatedAnimal[animalId] = {};
            }
            fieldNames.forEach(field => {
                if (idx > -1) {
                    saleStore.updateObjectKeyArrFieldValue('newSale', 'animals', idx, field, changes[field]);
                }
                if (idx2 > -1) {
                    saleStore.updateArrayKeyValue('visitorAnimals', idx2, field, changes[field]);
                }
                updatedAnimal[animalId][field] = true;
            });
            this.setState({ updatingAllNrr, updatingAnimal, updatedAnimal });
        } else {
            if (!saveErrorAnimal[animalId]) {
                saveErrorAnimal[animalId] = {};
            }
            fieldNames.forEach(field => { saveErrorAnimal[animalId][field] = true; });
            this.setState({ updatingAllNrr, updatingAnimal, saveErrorAnimal });
            appState.addToast({
                type: 'error',
                message: 'Kunne ikke lagre endringen. Sjekk internettforbindelsen og prøv igjen.',
                icon: 'triangle-exclamation',
            }, 8000);
        }
    }

    loadAnimalBookings = async (props = this.props) => {
        const { animals = [] } = props;
        const { calendarEventStore } = this.props.stores;
        const animalIds = animals.map(v => v.id);
        if (!animalIds || animalIds.length === 0) {
            return false;
        }
        const animalBookings = await calendarEventStore.load({ query: { animal: animalIds, future: 1 }, skipUpdate: 1 });
        calendarEventStore.updateKeyValue('animalBookings', animalBookings);
    }

    loadFilesFavorites = async (props = this.props) => {
        const { fileStore } = this.props.stores;
        fileStore.load({
            query: {
                isYours: true,
                isFavorite: true,
            },
            skipUpdate: false,
            limit: 3,
        });
    }

    loadAll = async (props = this.props) => {
        const { calendarEventStore, animalStore } = this.props.stores;
        calendarEventStore.updateKeyValue('animalBookings', []);
        await this.loadAnimalBookings(props);
        await this.loadFilesFavorites(props);

        // this.setState({ sendingToDyreId: false });

//         const { animals = [] } = props;
//         animalStore.updateKeyValue('currentAnimals', []);
//         const animalsIds = animals.map(v => v.id);
// console.log({ animalsIds, animals });
//         if (animalsIds && animalsIds.length > 0) {
//             const currentAnimals = await animalStore.load({ query: { id: animalsIds }, skipUpdate: 1 });
//             animalStore.updateKeyValue('currentAnimals', currentAnimals);
//         }
    }

    dyreIdGetChipInfo = async (chipId) => {
        const { animalStore } = this.props.stores;
        const { animal } = animalStore;
        const response = await animalStore.dyreIdGetChipInfo(chipId);
        const { data } = response;
        // console.log({ data });
    }

    updateAnimal = async e => {
        e.stopPropagation();
        e.preventDefault();
        const { id, field, value, field2, value2 } = e.target.closest('button').dataset;
        const { updatingAnimal, updatedAnimal, saveErrorAnimal } = this.state;
        if (!updatingAnimal[id]) {
            updatingAnimal[id] = {};
        }
        updatingAnimal[id][field] = true;
        // Clear any previous error for this field
        if (saveErrorAnimal[id]) {
            saveErrorAnimal[id][field] = false;
        }
        this.setState({ updatingAnimal, saveErrorAnimal });

        const { animalStore, saleStore, appState } = this.props.stores;
        const response = await animalStore.save({
            id: parseInt(id, 10),
            [field]: value,
            [field2]: value2,
        });

        updatingAnimal[id][field] = false;

        if (response && response.status < 300) {
            // Update the in-memory animals using the same method as onClickEditAnimal
            const animalIdInt = parseInt(id, 10);
            const { newSale, visitorAnimals } = saleStore;
            const { animals } = newSale;
            const idx = animals.findIndex(a => a.id === animalIdInt);
            if (idx > -1) {
                saleStore.updateObjectKeyArrFieldValue('newSale', 'animals', idx, field, value);
                saleStore.updateObjectKeyArrFieldValue('newSale', 'animals', idx, field2, value2);
            }
            const idx2 = visitorAnimals.findIndex(a => a.id === animalIdInt);
            if (idx2 > -1) {
                saleStore.updateArrayKeyValue('visitorAnimals', idx2, field, value);
                saleStore.updateArrayKeyValue('visitorAnimals', idx2, field2, value2);
            }

            if (!updatedAnimal[id]) {
                updatedAnimal[id] = {};
            }
            updatedAnimal[id][field] = true;
            this.setState({ updatingAnimal, updatedAnimal });
        } else {
            // Save failed - show error state
            if (!saveErrorAnimal[id]) {
                saveErrorAnimal[id] = {};
            }
            saveErrorAnimal[id][field] = true;
            this.setState({ updatingAnimal, saveErrorAnimal });
            appState.addToast({
                type: 'error',
                message: 'Kunne ikke lagre endringen. Sjekk internettforbindelsen og prøv igjen.',
                icon: 'triangle-exclamation',
            }, 8000);
        }
    }

    onClickPrintBarcode = e => {
        e.preventDefault();
        e.stopPropagation();
        const target = e.currentTarget || e.target.closest('button') || e.target.closest('i');
        const { value, name } = target.dataset;
        // console.log('onClickPrintSale', e);
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        // Render Receipt component with data
        const receipt = <IntlProvider definition={languageDef}>
            <Barcode value={value} /><br />
        </IntlProvider>;

        // Render to temporary div to capture HTML
        const tempDiv = document.createElement('div');
        tempDiv.className = 'receipt';
        document.body.appendChild(tempDiv);
        render(receipt, tempDiv);
        const htmlContent = tempDiv.outerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        // Trigger print dialog via iframe
        printViaIframe(htmlContent);
    }

    weightInput = e => {
        e.stopPropagation();
        e.preventDefault();
        const { value } = e.target;
        const { animal } = e.target.dataset;
        const animalId = parseInt(animal, 10);

        const internationalWeight = value.replace(',', '.');
        const finalWeight = parseFloat(internationalWeight);
        if (!isNaN(finalWeight) && !isNaN(animalId)) {
            const { weights } = this.state;
            this.setState({ weights: { ...weights, [animalId]: finalWeight } });
        }
    }

    weightSave = e => {
        e.stopPropagation();
        e.preventDefault();
        const { animal } = e.target.closest('i').dataset;
        const { weights, lastWeight } = this.state;
        const { animalStore } = this.props.stores;
        const animalId = parseInt(animal, 10);
        const weight = weights[animalId];

        if (weight) {
            animalStore.saveField(animalId, 'weight', weight, true);
            this.setState({ lastWeight: { ...lastWeight, [animalId]: weight } });
        }
    }

    toggleWeightView = async () => {
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const { settings = {} } = user;
        const current = settings.weightViewMode || 'graph';
        const newValue = current === 'graph' ? 'table' : 'graph';
        await userStore.updateSetting(
            { 'settings.weightViewMode': newValue },
            { weightViewMode: newValue },
        );
    }

    markChipAsChecked = e => {
        e.stopPropagation();
        e.preventDefault();
        const { animal } = e.target.closest('i').dataset;
        const { animalStore } = this.props.stores;
        const now = new Date();
        const nowIso = now.toISOString();
        animalStore.saveField(parseInt(animal, 10), 'chipIdChecked', nowIso, true);
    }

    editChipId = e => {
        e.stopPropagation();
        e.preventDefault();
        const { value } = e.target;
        const { animal } = e.target.closest('input').dataset;
        const { saleStore, animalStore } = this.props.stores;

        // Keep the controlled input's prop equal to what is typed: the debounced
        // save below re-renders this component, and Preact re-applies a stale
        // `value` prop to the DOM, wiping the digits typed so far (TICKET-01575).
        stageSaleAnimalChipId({ saleStore, animalStore, animalId: animal, value });

        if (this.editChipIdTimer) {
            clearTimeout(this.editChipIdTimer);
        }
        this.editChipIdTimer = setTimeout(() => {
            this.doEditChip(animal, value);
        }, 700);
    }

    doEditChip = (id, chipId) => {
        const { animalStore } = this.props.stores;
        animalStore.saveField(parseInt(id, 10), 'chipId', chipId, true);
    }

    componentDidMount() {
        this.loadAll();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.animals !== this.props.animals) {
            const nextIds = (nextProps.animals || []).map(a => a.id);
            const prevIds = (this.props.animals || []).map(a => a.id);
            const sameIds = nextIds.length === prevIds.length && nextIds.every(id => prevIds.includes(id));
            if (!sameIds) {
                this.setState({
                    dyreIdResponse: {},
                    nrrResponse: {},
                    checkingWithNrr: {},
                    updatingAllNrr: {},
                    checkingWithDyreId: {},
                    sentToDyreId: {},
                    sendingToDyreId: {},
                    updatingAnimal: {},
                    updatedAnimal: {},
                    saveErrorAnimal: {},
                    weights: {},
                    lastWeight: {},
                });
            }
            this.loadAll(nextProps);
        }
    }

    render() {
        const {
            smsSent,
            smsSending,
            checkingWithDyreId = {},
            sendingToDyreId = {},
            sentToDyreId = {},
            dyreIdResponse = {},
            checkingWithNrr = {},
            nrrResponse = {},
            updatingAllNrr = {},
            updatingAnimal = {},
            updatedAnimal = {},
            saveErrorAnimal = {},
            weights = {},
            lastWeight = {},
            showNewJournalConfirm,
        } = this.state;
        const {
            newJournalAreYouSure,
            animals: inputAnimals = [],
            journals = [],
            saleIsLocked = false,
            classNames = '',
            showNewJournal = true,
            showNewBooking = true,
            showNewNotification = true,
            showAdmission = true,
            journalsTitle,
            drawerLevel = 1,
            saveSale = () => {},
            showJournals = true,
        } = this.props;

        const { userStore, saleStore, calendarEventStore, animalStore, fileStore } = this.props.stores;
        const { user, isAdmin, customer } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const weightViewMode = util.getNestedValue(user, 'settings.weightViewMode') || 'graph';

        const { files } = fileStore;

        const { animalBookings } = calendarEventStore;
        const { currentAnimals, animals, saved = {} } = animalStore;

        let finalAnimals = inputAnimals;
        if (currentAnimals && currentAnimals.length > 0) {
            finalAnimals = currentAnimals;
        }

        const apiServer = util.getApiServer();
        const jwtToken = util.getJwtToken();

        const { newSale, calendarNotification } = saleStore;
        const { visitors = [] } = newSale;
        const currentVisitor = visitors[0] || {};
        const currentJournal = journals[0] || {};

        // console.log({ inputAnimals });
        // console.log({ animals });
        return (<>
            {(finalAnimals && finalAnimals.length > 0) && <>
                {finalAnimals.map(e => {
                    const animal = animals.find(a => a.id === e.id) || e;
                    const animalJournals = journals.filter(j => j.animal === animal.id);
                    const currentAnimalBookings = animalBookings && animalBookings.length > 0 ? animalBookings.filter(b => {
                        const bookingAnimals = b.animals ? b.animals.map(a => a.id) : [];
                        const isFound = bookingAnimals.includes(animal.id);
                        return isFound;
                    }) : [];

                    const dyreIdInfo = dyreIdResponse[animal.chipId];
                    const nrrInfo = nrrResponse[animal.chipId];
                    //  nkkId: dyreIdInfo.NKKId,
                    // "BirthDate": "29",
                    // "BirthMonth": "10",
                    // "BirthYear": "2021",
                    const dyreIdBirth = `${dyreIdInfo?.BirthYear}-${util.padDate(dyreIdInfo?.BirthMonth)}-${util.padDate(dyreIdInfo?.BirthDate)}`;
                    const animalBirth = animal.birth ? util.isoDate(animal.birth, false, false, true) : '';
                    const textColor = e.colorTag ? localUtil.isDark(e.colorTag) ? 'text-light' : 'text-dark' : '';

                    // console.log({ animal });
                    return (<>
                        <div class={`d-flex flex-column border box-container mt-1 p-1 bg-light ${classNames} font-style-narrow position-relative`}>
                            {(!saleIsLocked && animalJournals.length === 0) && <>
                                <div class='position-absolute' style='top: 5px; right: 10px; cursor: pointer;' onClick={this.onClickRemoveAnimal} data-id={animal.id}>
                                    <i class='fa-solid fa-xmark' />
                                </div>
                            </>}
                            {e && <>
                                {e.isLost && <div class='alert alert-danger py-1 px-2 mb-1 d-flex align-items-center'>
                                    <i class='fa-solid fa-triangle-exclamation me-2' />
                                    <strong><Text id='dyrejournal.animal.isLost-banner'>This animal is reported lost!</Text></strong>
                                </div>}
                                <div class={`d-flex flex-row justify-content-between px-3 py-0 animal-line ${e.isLost ? 'bg-danger text-white rounded-lg' : (e.isDececed ? 'bg-warning rounded-lg' : '')}`} onClick={this.onClickViewAnimal} data-id={animal.id}>
                                    <div class='me-2 animal-line' onClick={this.onClickEditAnimal} data-id={animal.id}>
                                        <i class='fa-solid fa-edit' />
                                    </div>
                                    <div class={`flex-fill ${e.colorTag ? 'rounded-pill px-2' : ''} ${textColor} ${e.colorTag ? 'dj-color-tag' : ''}`} style={`${e.colorTag ? `--dj-tag-color: ${e.colorTag};` : ''}`}>
                                        {e.isLost ? <i class='fa-duotone fa-magnifying-glass-location me-2' /> : ''}
                                        {e.isDececed ? <i class='fa-duotone fa-tombstone me-2' /> : ''}
                                        <span class='text-muted font-weight-lighter'>{animal.id}:</span> {animal.name} ({animal.species}/{animal.breed})
                                        {e.dyreIdPetId && <><span class='ms-2 font-weight-lighter text-muted'><small>DyreId</small></span></>}
                                    </div>
                                    <div class='ms-2 me-3'>
                                        <nobr>{animal.birth && <>{util.formatDate(animal.birth, { locale: 'nb-NO', day: '2-digit', month: '2-digit', year: 'numeric' })} ({animalAge(animal.birth)})</>}</nobr>
                                    </div>
                                </div>

                                {/* ChipId + Weight row (wraps on narrow screens) */}
                                <div class='d-flex flex-row flex-wrap align-items-center gap-2 px-3 py-1 mt-1'>
                                    <div class='input-group input-group-sm position-relative flex-nowrap' style='width: auto; flex: 0 1 auto;'>
                                        <div class='input-group-text rounded-pill-left py-0'>
                                            <small style='line-height: 1rem;'>
                                                <i class='fa-regular fa-microchip'></i>
                                            </small>
                                        </div>
                                        <input
                                            style='min-width: 11em; max-width: 14em; font-size: 0.8em;'
                                            class='form-control form-control-sm rounded-none py-0'
                                            type='text'
                                            placeholder='ChipId'
                                            data-animal={animal.id}
                                            value={animal.chipId}
                                            onInput={this.editChipId}
                                        />
                                        <button
                                            type='button'
                                            class={`btn btn-sm py-0 ${animal.chipIdChecked ? 'btn-success' : (animal.chipId ? 'btn-primary' : 'btn-secondary')} ${!animal.chipId ? 'rounded-pill-right' : ''}`}
                                            data-animal={animal.id}
                                            onClick={this.markChipAsChecked}
                                            title={animal.chipIdChecked ? 'Chip er verifisert (klikk for å oppdatere)' : 'Marker chip som verifisert lokalt'}
                                        >
                                            <i class={`fa-solid ${animal.chipIdChecked ? 'fa-check' : 'fa-scanner-touchscreen'}`} />
                                        </button>
                                        {animal.chipId && <>
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-outline-primary py-0'
                                                onClick={this.onClickCheckChipId}
                                                data-chipid={animal.chipId}
                                                data-animal={animal.id}
                                                title='Slå opp dyret hos DyreID'
                                            >
                                                <i class={`fa-solid fa-cloud-arrow-down ${checkingWithDyreId[animal.chipId] ? 'fa-spin' : ''}`} />
                                            </button>
                                            {canLookupNrr({ animal, featureFlags: customer?.featureFlags }) && <button
                                                type='button'
                                                class='btn btn-sm btn-outline-primary py-0'
                                                onClick={this.onClickCheckNrr}
                                                data-chipid={animal.chipId}
                                                data-animal={animal.id}
                                                title='Slå opp katten i NRR (Min Katt)'
                                            >
                                                <i class={`fa-solid fa-cat ${checkingWithNrr[animal.chipId] ? 'fa-spin' : ''}`} />
                                            </button>}
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-outline-secondary py-0'
                                                onClick={this.onClickPrintBarcode}
                                                data-value={animal.chipId}
                                                data-name={'chipId'}
                                                title='Skriv ut strekkode med chip-ID'
                                            >
                                                <i class='fa-solid fa-print' />
                                            </button>
                                            <a
                                                class='btn btn-sm btn-outline-secondary rounded-pill-right py-0'
                                                href={`${apiServer}/api/dyreid/identitycard/${animal.id}/pdf?jwtToken=${jwtToken}`}
                                                target='_blank'
                                                rel='noreferrer'
                                                title='Skriv ut identitetskort'
                                            >
                                                <i class='fa-solid fa-id-card' />
                                            </a>
                                        </>}
                                    </div>
                                    <div class='input-group input-group-sm position-relative ms-auto' style='width: auto; flex: 0 1 auto;'>
                                        <div class='input-group-text rounded-pill-left py-0'>
                                            <small style='line-height: 1rem;'>
                                                <i class='fa-regular fa-scale-unbalanced'></i>
                                            </small>
                                        </div>
                                        <input type='text' style='min-width: 5em; max-width: 7em;' class='form-control form-control-sm rounded-none py-0' placeholder='Vekt' data-animal={animal.id} onInput={this.weightInput} />
                                        <div class={`input-group-text rounded-pill-right py-0 ${weights[animal.id] ? 'bg-primary text-white' : 'bg-secondary text-muted'}`} style='cursor: pointer;' title='Lagre vekt'>
                                            <i
                                                class='fa-solid fa-save'
                                                data-animal={animal.id}
                                                onClick={this.weightSave}
                                            />
                                        </div>
                                    </div>
                                    {(animal.weight || lastWeight[animal.id]) && <small class='text-body-secondary' style='white-space: nowrap;'>{lastWeight[animal.id] || animal.weight} kg</small>}
                                </div>

                                {(() => {
                                    const missingFields = [];
                                    if (!animal.name) missingFields.push('name');
                                    if (!animal.birth) missingFields.push('birth');
                                    if (!animal.chipId) missingFields.push('chipId');
                                    if (!animal.gender) missingFields.push('gender');
                                    if (!animal.speciesId) missingFields.push('speciesId');
                                    if (!animal.breedId) missingFields.push('breedId');

                                    if (missingFields.length > 0) {
                                        return (
                                            <div class='d-flex flex-row align-items-center px-3 py-1 mt-1'>
                                                <div class='text-danger' style='font-size: 0.75em;'>
                                                    <i class='fa-solid fa-triangle-exclamation' /> <Text id='dyrejournal.animal.missing-fields'>Missing fields</Text>: {missingFields.join(', ')}
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}

                                {dyreIdInfo && <div class='d-flex flex-column justify-content-between px-3 py-0  mt-2 animal-line'>
                                    <div class='d-flex flex-row justify-content-end'>
                                        <i class='fa-solid fa-times text-danger' onClick={this.closeDyreIdInfo} data-chipid={e.chipId} />
                                    </div>
                                    {dyreIdInfo.IsSuccess === 'true' || dyreIdInfo.IsSuccess ? <>
                                        <div class='alert alert-success rounded-lg mb-0'>
                                            <Text id='dyrejournal.dyreid-success'>Animal found in DyreID</Text>
                                        </div>
                                        {dyreIdInfo.IsLost === 'true' && <div class='alert alert-danger rounded-lg mt-2 mb-0 d-flex align-items-center'>
                                            <i class='fa-solid fa-triangle-exclamation me-2' />
                                            <div>
                                                <strong><Text id='dyrejournal.dyreid-isLost'>Reported lost in DyreID!</Text></strong>
                                                <div><small><Text id='dyrejournal.dyreid-isLost-help'>Owner has marked this animal as lost. Verify identity before any treatment.</Text></small></div>
                                            </div>
                                        </div>}

                                        {(() => {
                                            // Check if owner matches
                                            const dyreIdOwner = dyreIdInfo.OwnerFullName?.trim().toLowerCase();
                                            const localOwnerName = currentVisitor?.name?.trim().toLowerCase();
                                            const localOwnerPhone = currentVisitor?.cellphone?.replace(/\s/g, '');
                                            const dyreIdPhone = dyreIdInfo.PrimaryTelephoneNo?.replace(/[\s\+]/g, '');

                                            const ownerNameMatches = dyreIdOwner && localOwnerName && dyreIdOwner === localOwnerName;
                                            const ownerPhoneMatches = dyreIdPhone && localOwnerPhone &&
                                                (dyreIdPhone.includes(localOwnerPhone) || localOwnerPhone.includes(dyreIdPhone));

                                            if (!ownerNameMatches && !ownerPhoneMatches && (dyreIdOwner || dyreIdPhone)) {
                                                return (
                                                    <div class='alert alert-danger mt-2 mb-2'>
                                                        <div class='d-flex flex-row align-items-center'>
                                                            <i class='fa-solid fa-triangle-exclamation me-2' />
                                                            <div>
                                                                <strong><Text id='dyrejournal.dyreid-owner-mismatch'>Warning: Owner does not match!</Text></strong>
                                                                <div class='mt-1'>
                                                                    <small>
                                                                        <strong><Text id='dyrejournal.dyreid-owner'>DyreID owner</Text>:</strong> {dyreIdInfo.OwnerFullName}
                                                                        {dyreIdInfo.PrimaryTelephoneNo && <> ({dyreIdInfo.PrimaryTelephoneNo})</>}
                                                                    </small>
                                                                </div>
                                                                <div>
                                                                    <small>
                                                                        <strong><Text id='dyrejournal.local-owner'>Your owner</Text>:</strong> {currentVisitor?.name || <Text id='input.not-set'>Not set</Text>}
                                                                        {currentVisitor?.cellphone && <> ({currentVisitor.cellphone})</>}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}

                                        <div class='d-flex flex-row justify-content-between'>
                                            <div class='font-weight-lighter text-muted'>
                                                <small>
                                                    <Text id='dyrejournal.dyreid-info-about'>Info from DyreID compared to your info.</Text>
                                                </small>
                                            </div>
                                        </div>
                                        {/* table-responsive keeps this 5-column table scrolling inside its own
                                            box instead of pushing the whole page sideways on a phone, and
                                            compare-table pins the update column to the right edge so the
                                            "Update" buttons stay reachable. */}
                                        <div class='small table-responsive'>
                                            <table class='table table-sm table-striped table-bordered compare-table'>
                                                <thead>
                                                    <tr>
                                                        <th>&nbsp;</th>
                                                        <th><Text id='dyrejournal.dyrejournal-info'>Your info</Text></th>
                                                        <th>&nbsp;</th>
                                                        <th><Text id='dyrejournal.dyreid-info'>DyreID</Text></th>
                                                        <th>&nbsp;</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {/* <tr>
                                                        <td>{animal.owner}</td>
                                                        <td class='font-italic'>{dyreIdInfo.OwnerFullName}</td>
                                                        <td>&nbsp;</td>
                                                    </tr> */}
                                                    <tr>
                                                        <td class='font-weight-lighter'><Text id='input.name'>Name</Text></td>
                                                        <td>{animal.name}</td>
                                                        <td class='text-center'>
                                                            <i class='fa-solid fa-arrow-right' />
                                                        </td>
                                                        <td class='font-italic'>{dyreIdInfo.Pedigree}</td>
                                                        <td class='text-center'>
                                                            {updatingAnimal[animal.id] && updatingAnimal[animal.id].name ? <>
                                                                <i class='fa-solid fa-spin fa-spinner' />
                                                            </> : <>
                                                                {saveErrorAnimal[animal.id] && saveErrorAnimal[animal.id].name ? <>
                                                                    <button
                                                                        class='btn btn-link btn-sm p-0 text-danger'
                                                                        onClick={this.updateAnimal}
                                                                        data-id={animal.id}
                                                                        data-field='name'
                                                                        data-value={dyreIdInfo.Pedigree}
                                                                    >
                                                                        <i class='fa-solid fa-triangle-exclamation' /> Prøv igjen
                                                                    </button>
                                                                </> : <>
                                                                    {updatedAnimal[animal.id] && updatedAnimal[animal.id].name ? <>
                                                                        <i class='fa-solid fa-check text-success' />
                                                                    </> : <>
                                                                        {animal.name !== dyreIdInfo.Pedigree ? <>
                                                                            <button
                                                                                class='btn btn-link btn-sm p-0'
                                                                                onClick={this.updateAnimal}
                                                                                data-id={animal.id}
                                                                                data-field='name'
                                                                                data-value={dyreIdInfo.Pedigree}
                                                                            >
                                                                                <Text id='dyrejournal.dyreid-update'>Update</Text>
                                                                            </button>
                                                                        </> : <>
                                                                            <i class='fa-solid fa-check text-success' />
                                                                        </>}
                                                                    </>}
                                                                </>}
                                                            </>}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td class='font-weight-lighter'><Text id='input.pedigree-name'>Pedigree name</Text></td>
                                                        <td>{animal.pedigreeName}</td>
                                                        <td class='text-center'>
                                                            <i class='fa-solid fa-arrow-right' />
                                                        </td>
                                                        <td class='font-italic'>{dyreIdInfo.PetName}</td>
                                                        <td class='text-center'>
                                                            {updatingAnimal[animal.id] && updatingAnimal[animal.id].pedigreeName ? <>
                                                                <i class='fa-solid fa-spin fa-spinner' />
                                                            </> : <>
                                                                {saveErrorAnimal[animal.id] && saveErrorAnimal[animal.id].pedigreeName ? <>
                                                                    <button
                                                                        class='btn btn-link btn-sm p-0 text-danger'
                                                                        onClick={this.updateAnimal}
                                                                        data-id={animal.id}
                                                                        data-field='pedigreeName'
                                                                        data-value={dyreIdInfo.PetName}
                                                                    >
                                                                        <i class='fa-solid fa-triangle-exclamation' /> Prøv igjen
                                                                    </button>
                                                                </> : <>
                                                                    {updatedAnimal[animal.id] && updatedAnimal[animal.id].pedigreeName ? <>
                                                                        <i class='fa-solid fa-check text-success' />
                                                                    </> : <>
                                                                        {animal.pedigreeName !== dyreIdInfo.PetName ? <>
                                                                            <button
                                                                                class='btn btn-link btn-sm p-0'
                                                                                onClick={this.updateAnimal}
                                                                                data-id={animal.id}
                                                                                data-field='pedigreeName'
                                                                                data-value={dyreIdInfo.PetName}
                                                                            >
                                                                                <Text id='dyrejournal.dyreid-update'>Update</Text>
                                                                            </button>
                                                                        </> : <>
                                                                            <i class='fa-solid fa-check text-success' />
                                                                        </>}
                                                                    </>}
                                                                </>}
                                                            </>}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td class='font-weight-lighter'><Text id='input.birth'>Birth</Text></td>
                                                        <td>{animalBirth}</td>
                                                        <td class='text-center'>
                                                            <i class='fa-solid fa-arrow-right' />
                                                        </td>
                                                        <td class='font-italic'>{dyreIdBirth}</td>
                                                        <td class='text-center'>
                                                            {updatingAnimal[animal.id] && updatingAnimal[animal.id].birth ? <>
                                                                <i class='fa-solid fa-spin fa-spinner' />
                                                            </> : <>
                                                                {saveErrorAnimal[animal.id] && saveErrorAnimal[animal.id].birth ? <>
                                                                    <button
                                                                        class='btn btn-link btn-sm p-0 text-danger'
                                                                        onClick={this.updateAnimal}
                                                                        data-id={animal.id}
                                                                        data-field='birth'
                                                                        data-value={dyreIdBirth}
                                                                    >
                                                                        <i class='fa-solid fa-triangle-exclamation' /> Prøv igjen
                                                                    </button>
                                                                </> : <>
                                                                    {updatedAnimal[animal.id] && updatedAnimal[animal.id].birth ? <>
                                                                        <i class='fa-solid fa-check text-success' />
                                                                    </> : <>
                                                                        {animalBirth !== dyreIdBirth ? <>
                                                                            <button
                                                                                class='btn btn-link btn-sm p-0'
                                                                                onClick={this.updateAnimal}
                                                                                data-id={animal.id}
                                                                                data-field='birth'
                                                                                data-value={dyreIdBirth}
                                                                            >
                                                                                <Text id='dyrejournal.dyreid-update'>Update</Text>
                                                                            </button>
                                                                        </> : <>
                                                                            <i class='fa-solid fa-check text-success' />
                                                                        </>}
                                                                    </>}
                                                                </>}
                                                            </>}
                                                        </td>
                                                    </tr>
                                                    {(() => {
                                                        const mappedGender = mapDyreIdGender(dyreIdInfo.PetSex);
                                                        // Don't offer to overwrite a registered gender with an empty value
                                                        // from DyreID — that's a foot-gun, not a sync.
                                                        const canPushGender = !!mappedGender && animal.gender !== mappedGender;
                                                        return (
                                                            <tr>
                                                                <td class='font-weight-lighter'><Text id='input.gender'>Gender</Text></td>
                                                                <td>{animal.gender}</td>
                                                                <td class='text-center'>
                                                                    <i class='fa-solid fa-arrow-right' />
                                                                </td>
                                                                <td class='font-italic'>
                                                                    {mappedGender}
                                                                    {mappedGender !== dyreIdInfo.PetSex && (
                                                                        <small class='text-muted d-block'>({dyreIdInfo.PetSex})</small>
                                                                    )}
                                                                </td>
                                                                <td class='text-center'>
                                                                    {updatingAnimal[animal.id] && updatingAnimal[animal.id].gender ? <>
                                                                        <i class='fa-solid fa-spin fa-spinner' />
                                                                    </> : <>
                                                                        {saveErrorAnimal[animal.id] && saveErrorAnimal[animal.id].gender ? <>
                                                                            <button
                                                                                class='btn btn-link btn-sm p-0 text-danger'
                                                                                onClick={this.updateAnimal}
                                                                                data-id={animal.id}
                                                                                data-field='gender'
                                                                                data-value={mappedGender}
                                                                            >
                                                                                <i class='fa-solid fa-triangle-exclamation' /> Prøv igjen
                                                                            </button>
                                                                        </> : <>
                                                                            {updatedAnimal[animal.id] && updatedAnimal[animal.id].gender ? <>
                                                                                <i class='fa-solid fa-check text-success' />
                                                                            </> : <>
                                                                                {canPushGender ? <>
                                                                                    <button
                                                                                        class='btn btn-link btn-sm p-0'
                                                                                        onClick={this.updateAnimal}
                                                                                        data-id={animal.id}
                                                                                        data-field='gender'
                                                                                        data-value={mappedGender}
                                                                                    >
                                                                                        <Text id='dyrejournal.dyreid-update'>Update</Text>
                                                                                    </button>
                                                                                </> : <>
                                                                                    <i class='fa-solid fa-check text-success' />
                                                                                </>}
                                                                            </>}
                                                                        </>}
                                                                    </>}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })()}
                                                    <tr>
                                                        <td class='font-weight-lighter'><Text id='input.species'>Species</Text></td>
                                                        <td>{animal.species}</td>
                                                        <td class='text-center'>
                                                            <i class='fa-solid fa-arrow-right' />
                                                        </td>
                                                        <td class='font-italic'>{dyreIdInfo.Species}</td>
                                                        <td class='text-center'>
                                                            {updatingAnimal[animal.id] && updatingAnimal[animal.id].species ? <>
                                                                <i class='fa-solid fa-spin fa-spinner' />
                                                            </> : <>
                                                                {saveErrorAnimal[animal.id] && saveErrorAnimal[animal.id].species ? <>
                                                                    <button
                                                                        class='btn btn-link btn-sm p-0 text-danger'
                                                                        onClick={this.updateAnimal}
                                                                        data-id={animal.id}
                                                                        data-field='species'
                                                                        data-value={dyreIdInfo.Species}
                                                                        data-field2='speciesId'
                                                                        data-value2={dyreIdInfo.SpeciesCode}
                                                                    >
                                                                        <i class='fa-solid fa-triangle-exclamation' /> Prøv igjen
                                                                    </button>
                                                                </> : <>
                                                                    {updatedAnimal[animal.id] && updatedAnimal[animal.id].species ? <>
                                                                        <i class='fa-solid fa-check text-success' />
                                                                    </> : <>
                                                                        {animal.species !== dyreIdInfo.Species || animal.speciesId !== parseInt(dyreIdInfo.SpeciesCode, 10) ? <>
                                                                            <button
                                                                                class='btn btn-link btn-sm p-0'
                                                                                onClick={this.updateAnimal}
                                                                                data-id={animal.id}
                                                                                data-field='species'
                                                                                data-value={dyreIdInfo.Species}
                                                                                data-field2='speciesId'
                                                                                data-value2={dyreIdInfo.SpeciesCode}
                                                                            >
                                                                                <Text id='dyrejournal.dyreid-update-to'>Update to</Text> "{dyreIdInfo.Species}"
                                                                            </button>
                                                                        </> : <>
                                                                            <i class='fa-solid fa-check text-success' />
                                                                        </>}
                                                                    </>}
                                                                </>}
                                                            </>}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td class='font-weight-lighter'><Text id='input.breed'>Breed</Text></td>
                                                        <td>{animal.breed}</td>
                                                        <td class='text-center'>
                                                            <i class='fa-solid fa-arrow-right' />
                                                        </td>
                                                        <td class='font-italic'>{dyreIdInfo.Breed}</td>
                                                        <td class='text-center'>
                                                            {updatingAnimal[animal.id] && updatingAnimal[animal.id].breed ? <>
                                                                <i class='fa-solid fa-spin fa-spinner' />
                                                            </> : <>
                                                                {saveErrorAnimal[animal.id] && saveErrorAnimal[animal.id].breed ? <>
                                                                    <button
                                                                        class='btn btn-link btn-sm p-0 text-danger'
                                                                        onClick={this.updateAnimal}
                                                                        data-id={animal.id}
                                                                        data-field='breed'
                                                                        data-value={dyreIdInfo.Breed}
                                                                        data-field2='breedId'
                                                                        data-value2={dyreIdInfo.BreedCode}
                                                                    >
                                                                        <i class='fa-solid fa-triangle-exclamation' /> Prøv igjen
                                                                    </button>
                                                                </> : <>
                                                                    {updatedAnimal[animal.id] && updatedAnimal[animal.id].breed ? <>
                                                                        <i class='fa-solid fa-check text-success' />
                                                                    </> : <>
                                                                        {animal.breed !== dyreIdInfo.Breed || animal.breedId !== parseInt(dyreIdInfo.BreedCode, 10) ? <>
                                                                            <button
                                                                                class='btn btn-link btn-sm p-0'
                                                                                onClick={this.updateAnimal}
                                                                                data-id={animal.id}
                                                                                data-field='breed'
                                                                                data-value={dyreIdInfo.Breed}
                                                                                data-field2='breedId'
                                                                                data-value2={dyreIdInfo.BreedCode}
                                                                            >
                                                                                <Text id='dyrejournal.dyreid-update-to'>Update to</Text> "{dyreIdInfo.Breed}"
                                                                            </button>
                                                                        </> : <>
                                                                            <i class='fa-solid fa-check text-success' />
                                                                        </>}
                                                                    </>}
                                                                </>}
                                                            </>}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td class='font-weight-lighter'><Text id='input.nkkid'>NKK ID</Text></td>
                                                        <td>{animal.nkkId}</td>
                                                        <td class='text-center'>
                                                            <i class='fa-solid fa-arrow-right' />
                                                        </td>
                                                        <td class='font-italic'>{dyreIdInfo.NKKId}</td>
                                                        <td class='text-center'>
                                                            {updatingAnimal[animal.id] && updatingAnimal[animal.id].nkkId ? <>
                                                                <i class='fa-solid fa-spin fa-spinner' />
                                                            </> : <>
                                                                {saveErrorAnimal[animal.id] && saveErrorAnimal[animal.id].nkkId ? <>
                                                                    <button
                                                                        class='btn btn-link btn-sm p-0 text-danger'
                                                                        onClick={this.updateAnimal}
                                                                        data-id={animal.id}
                                                                        data-field='nkkId'
                                                                        data-value={dyreIdInfo.NKKId}
                                                                    >
                                                                        <i class='fa-solid fa-triangle-exclamation' /> Prøv igjen
                                                                    </button>
                                                                </> : <>
                                                                    {updatedAnimal[animal.id] && updatedAnimal[animal.id].nkkId ? <>
                                                                        <i class='fa-solid fa-check text-success' />
                                                                    </> : <>
                                                                        {animal.nkkId !== dyreIdInfo.NKKId ? <>
                                                                            <button
                                                                                class='btn btn-link btn-sm p-0'
                                                                                onClick={this.updateAnimal}
                                                                                data-id={animal.id}
                                                                                data-field='nkkId'
                                                                                data-value={dyreIdInfo.NKKId}
                                                                            >
                                                                                <Text id='dyrejournal.dyreid-update'>Update</Text>
                                                                            </button>
                                                                        </> : <>
                                                                            <i class='fa-solid fa-check text-success' />
                                                                        </>}
                                                                    </>}
                                                                </>}
                                                            </>}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td class='font-weight-lighter'><Text id='input.ownerName'>Owner name</Text></td>
                                                        <td colspan='2'>&nbsp;</td>
                                                        <td>{dyreIdInfo.OwnerFullName}</td>
                                                        <td>&nbsp;</td>
                                                    </tr>
                                                    <tr>
                                                        <td class='font-weight-lighter'><Text id='input.ownerCellphone'>Owner cellphone</Text></td>
                                                        <td colspan='2'>&nbsp;</td>
                                                        <td>{dyreIdInfo.PrimaryTelephoneNo}</td>
                                                        <td>&nbsp;</td>
                                                    </tr>
                                                    <tr>
                                                        <td class='font-weight-lighter'><Text id='input.ownerEmail'>Owner email</Text></td>
                                                        <td colspan='2'>&nbsp;</td>
                                                        <td>{dyreIdInfo.PrimaryEmail}</td>
                                                        <td>&nbsp;</td>
                                                    </tr>
                                                    <tr>
                                                        <td class='font-weight-lighter'><Text id='input.ownerAddress'>Owner address</Text></td>
                                                        <td colspan='2'>&nbsp;</td>
                                                        <td>{dyreIdInfo.AddressLine1}, {dyreIdInfo.PostalCode} {dyreIdInfo.City}, {dyreIdInfo.State}</td>
                                                        <td>&nbsp;</td>
                                                    </tr>

                                                </tbody>
                                            </table>
                                        </div>

                                        {/* {animal.breed} === {dyreIdInfo.Breed}<br />
                                        {animal.breedId} === {dyreIdInfo.BreedCode}<br />
                                        {animal.Species} === {dyreIdInfo.Species}<br />
                                        {animal.speciesId} === {dyreIdInfo.SpeciesCode}<br /> */}
                                    </> : <>
                                        <div class='alert alert-danger rounded-lg'>
                                            <Text id='dyrejournal.dyreid-error'>Animal not found in DyreID</Text>
                                            {isAdmin && <>
                                                <pre class='mb-0 mt-2' style='font-size: 0.7em; white-space: pre-wrap; word-break: break-word; overflow-wrap: anywhere;'>{JSON.stringify(dyreIdInfo, null, 2)}</pre>
                                            </>}
                                        </div>
                                    </>}

                                    {/* <xmp>{JSON.stringify(dyreIdInfo, null, 2)}</xmp> */}

                                    {/* {
                                        "IsSuccess": "true",
                                        "AddressLine1": "Nord-Torpveien 1561",
                                        "BirthDate": "29",
                                        "BirthMonth": "10",
                                        "BirthYear": "2021",
                                        "Breed": "Ukjent",
                                        "BreedCode": "609",
                                        "ChipId": "578098100762746",
                                        "City": "Skedsmokorset",
                                        "Contacts": {
                                            "ContactInfo": {
                                            "ContactNumber": "90169741",
                                            "ContactType": "2"
                                            }
                                        },
                                        "Country": "+47",
                                        "IsDead": "false",
                                        "IsLost": "false",
                                        "OwnerFirstName": "Marius",
                                        "OwnerFullName": "Marius Hagen",
                                        "OwnerLastName": "Hagen",
                                        "Pedigree": "Solbergmutt's Iditarod Ariel",
                                        "PetName": "Solbergmutt's Iditarod Ariel",
                                        "PostalCode": "2880",
                                        "PrimaryTelephoneNo": "+47 90169741",
                                        "RegDato": null,
                                        "Species": "Hund",
                                        "SpeciesCode": "1",
                                        "State": "Akershus"
                                    } */}


                                </div>}
                                {nrrInfo && <div class='d-flex flex-column justify-content-between px-3 py-0 mt-2 animal-line'>
                                    <div class='d-flex flex-row justify-content-end'>
                                        <i class='fa-solid fa-times text-danger' onClick={this.closeNrrInfo} data-chipid={animal.chipId} />
                                    </div>
                                    {nrrInfo.status === 200 && nrrInfo.data?.count > 0 ? <>
                                        <div class='alert alert-success rounded-lg mb-0'>
                                            <Text id='dyrejournal.nrr-success'>Cat found in NRR</Text>
                                        </div>
                                        {nrrInfo.data.cats.map((cat, catIdx) => {
                                            const rows = buildNrrRows(animal, cat);
                                            const hasChanges = rows.some(r => r.differs);
                                            return (<div class='mt-2' key={cat.registrationNumber || cat.chipId || catIdx}>
                                                <div class='d-flex flex-row justify-content-between align-items-center'>
                                                    <div class='font-weight-lighter text-muted'>
                                                        <small><Text id='dyrejournal.nrr-info-about'>Info from NRR compared to your info.</Text></small>
                                                        <small class='ms-2'>Chip: {cat.chipId}</small>
                                                    </div>
                                                    {hasChanges && <button
                                                        type='button'
                                                        class='btn btn-sm btn-primary py-0'
                                                        onClick={this.updateAnimalFromNrr}
                                                        data-id={animal.id}
                                                        data-chipid={animal.chipId}
                                                        data-index={catIdx}
                                                        disabled={updatingAllNrr[animal.id]}
                                                    >
                                                        {updatingAllNrr[animal.id] ? <i class='fa-solid fa-spin fa-spinner me-1' /> : <i class='fa-solid fa-cloud-arrow-down me-1' />}
                                                        <Text id='dyrejournal.nrr-update-all'>Update all</Text>
                                                    </button>}
                                                </div>
                                                <div class='small table-responsive'>
                                                    <table class='table table-sm table-striped table-bordered mt-1 compare-table'>
                                                        <thead>
                                                            <tr>
                                                                <th>&nbsp;</th>
                                                                <th><Text id='dyrejournal.dyrejournal-info'>Your info</Text></th>
                                                                <th>&nbsp;</th>
                                                                <th><Text id='dyrejournal.nrr-info'>NRR</Text></th>
                                                                <th>&nbsp;</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {rows.map(row => (<tr key={row.field}>
                                                                <td class='font-weight-lighter'><Text id={row.labelId}>{row.labelDefault}</Text></td>
                                                                <td>{row.local}</td>
                                                                <td class='text-center'><i class='fa-solid fa-arrow-right' /></td>
                                                                <td class='font-italic'>{row.nrr}</td>
                                                                <td class='text-center'>
                                                                    {updatingAnimal[animal.id] && updatingAnimal[animal.id][row.field] ? <>
                                                                        <i class='fa-solid fa-spin fa-spinner' />
                                                                    </> : <>
                                                                        {saveErrorAnimal[animal.id] && saveErrorAnimal[animal.id][row.field] ? <>
                                                                            <button
                                                                                class='btn btn-link btn-sm p-0 text-danger'
                                                                                onClick={this.updateAnimal}
                                                                                data-id={animal.id}
                                                                                data-field={row.field}
                                                                                data-value={row.value}
                                                                            >
                                                                                <i class='fa-solid fa-triangle-exclamation' /> Prøv igjen
                                                                            </button>
                                                                        </> : <>
                                                                            {(updatedAnimal[animal.id] && updatedAnimal[animal.id][row.field]) || !row.differs ? <>
                                                                                <i class='fa-solid fa-check text-success' />
                                                                            </> : <>
                                                                                <button
                                                                                    class='btn btn-link btn-sm p-0'
                                                                                    onClick={this.updateAnimal}
                                                                                    data-id={animal.id}
                                                                                    data-field={row.field}
                                                                                    data-value={row.value}
                                                                                >
                                                                                    <Text id='dyrejournal.dyreid-update'>Update</Text>
                                                                                </button>
                                                                            </>}
                                                                        </>}
                                                                    </>}
                                                                </td>
                                                            </tr>))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <div class='text-muted'><small><Text id='dyrejournal.nrr-source'>Source: NRR Min Katt</Text></small></div>
                                            </div>);
                                        })}
                                    </> : <>
                                        {nrrInfo.status === 200 ? <>
                                            <div class='alert alert-secondary rounded-lg mb-0'>
                                                <Text id='dyrejournal.nrr-not-found'>Cat not found in NRR</Text>
                                            </div>
                                        </> : <>
                                            <div class='alert alert-danger rounded-lg mb-0'>
                                                <Text id='dyrejournal.nrr-error'>NRR lookup failed</Text>: {nrrInfo.message}
                                            </div>
                                        </>}
                                    </>}
                                </div>}
                                {(animal.cave || animal.isAggressive == 1) ? <>
                                    <div class={`d-flex flex-row justify-content-between px-3 py-0  mt-2 animal-line`} onClick={this.onClickEditAnimal} data-id={animal.id}>
                                        <div class='flex-fill'>
                                            <div class='p-2 mb-2 bg-danger text-white rounded-lg d-flex flex-column'>
                                                {animal.cave ? <>
                                                    <div>
                                                        CAVE: {animal.cave}
                                                    </div>
                                                </> : <>
                                                </>}
                                                {animal.isAggressive ? <>
                                                    <div>
                                                        <i class='fa-solid fa-exclamation-triangle me-2' /> <Text id='dyrejournal.aggressive'>Aggressive behavior</Text>
                                                    </div>
                                                </> : <>
                                                </>}

                                            </div>
                                        </div>
                                    </div>
                                </> : <></>}
                                {animal.importantInfo ? <>
                                    <div class={`d-flex flex-row justify-content-between px-3 py-0 mt-2 animal-line`} onClick={this.onClickEditAnimal} data-id={animal.id}>
                                        <div class='flex-fill'>
                                            <div class='p-2 mb-2 bg-info text-white rounded-lg d-flex flex-column'>
                                                <div>
                                                    <i class='fa-solid fa-info-circle me-2' /> <strong><Text id='dyrejournal.important-info'>Important info</Text>:</strong> {animal.importantInfo}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </> : <></>}
                            </>}
                            {/* Compact bookings section */}
                            {(calendarNotification?.id || (currentAnimalBookings && currentAnimalBookings.length > 0)) && <>
                                <div class='d-flex flex-column px-3 py-1 mt-1'>
                                    {calendarNotification?.id && <>
                                        <small class='text-muted'>
                                            <i class='fa-solid fa-calendar-check me-1' />
                                            <span class='calendar-event' onClick={this.onClickViewBooking} data-id={calendarNotification.id}>
                                                {util.formatDate(calendarNotification.start, { day: 'numeric', month: 'short', locale: 'nb-NO' })} {util.isoTime(calendarNotification.start)} - {calendarNotification.title}
                                            </span>
                                        </small>
                                    </>}
                                    {currentAnimalBookings && currentAnimalBookings.length > 0 && currentAnimalBookings.map(b => {
                                        const isNotification = b.calendarId === 'Innkallinger';
                                        return (<>
                                            <small class='text-muted'>
                                                <i class={`fa-duotone ${isNotification ? 'fa-bell' : 'fa-calendar'} me-1`} />
                                                <span class='calendar-event' onClick={this.onClickViewBooking} data-id={b.id}>
                                                    {util.formatDate(b.start, { day: 'numeric', month: 'short', locale: 'nb-NO' })} {!isNotification && util.isoTime(b.start)} - {b.title}
                                                </span>
                                                {!isNotification && <>
                                                    <button
                                                        type='button'
                                                        class={`btn btn-sm btn-${smsSent[b.id] || b.reminderSMSSent || b.autoReminderSMSSent ? 'success' : 'outline-primary'} rounded-pill ms-2 py-0`}
                                                        style='font-size: 0.65rem; line-height: 0.65rem;'
                                                        onClick={this.onClickSendBookingSms}
                                                        data-id={b.id}
                                                        disabled={smsSent[b.id]}
                                                    >
                                                        <i class='fa-solid fa-sms' />
                                                        {smsSending[b.id] && <i class='fa-solid fa-spinner-third fa-spin ms-1' />}
                                                        {(smsSent[b.id] || b.reminderSMSSent || b.autoReminderSMSSent) && <i class='fa-solid fa-check ms-1' />}
                                                    </button>
                                                </>}
                                            </small>
                                        </>);
                                    })}
                                </div>
                            </>}

                            {animal.allWeights && animal.allWeights.length > 2 && <>
                                <div class='d-flex flex-row justify-content-end px-1 py-0 mt-1'>
                                    <button
                                        class='btn btn-sm btn-link text-body-secondary py-0 px-1'
                                        onClick={this.toggleWeightView}
                                        title={weightViewMode === 'graph' ? 'Show table' : 'Show graph'}
                                    >
                                        <i class={`fa-solid ${weightViewMode === 'graph' ? 'fa-table' : 'fa-chart-line'}`} />
                                    </button>
                                </div>
                                {weightViewMode === 'graph' ? (
                                    <div class='d-flex flex-row justify-content-end px-1 py-0'>
                                    <LineGraphMini
                                        stores={this.props.stores}
                                        dataLeft={[animal.allWeights.sort(sortByDateAsc).map(d => ({
                                            epoch: Math.floor(new Date(d.date).getTime()),
                                            x: Math.floor(new Date(d.date).getTime()),
                                            y: d.weight
                                        }))]}
                                        legendLeft={['Vekt']}
                                        unitLeft={['kg']}
                                        yTicksLeftVal={val => `${val} kg`}
                                        xTicksVal={val => new Date(val).toLocaleString('nb-NO', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: '2-digit',
                                        })}
                                        width={animal.allWeights.length > 10 ? 600 : 300}
                                        height={50}
                                        smoothLeft={[true]}
                                        gradientFillLeft={['#0d9488']}
                                        gradientOpacity={0.15}
                                        inputStyleLeft={'stroke: #0d9488 !important; stroke-width: 2px !important;'}
                                        showValueLeftLine={true}
                                    />
                                    </div>
                                ) : (
                                    <div class='px-1' style='line-height: 1.2; max-height: 100px; overflow-y: auto;'>
                                        <table class='table table-sm mb-0' style='font-size: 0.75rem;'>
                                            <thead>
                                                <tr>
                                                    <th class='fw-normal text-body-secondary py-0'><Text id='dyrejournal.weight-date'>Date</Text></th>
                                                    <th class='fw-normal text-body-secondary text-end py-0'><Text id='dyrejournal.weight-kg'>Weight (kg)</Text></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {[...animal.allWeights].sort(sortByDateAsc).reverse().map(w => (
                                                    <tr>
                                                        <td class='py-0'>{new Date(w.date).toLocaleString('nb-NO', {
                                                            day: '2-digit',
                                                            month: 'short',
                                                            year: '2-digit',
                                                        })}</td>
                                                        <td class='text-end py-0'>{w.weight} kg</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>}

                            {showJournals && <DyrejournalJournalListMini
                                classNames='mt-2'
                                stores={this.props.stores}
                                journals={animalJournals.reverse()}
                                drawerLevel={drawerLevel}
                                // saleIsLocked={saleIsLocked}
                                animals={[e]}
                                collapse={true}
                                saveSale={saveSale}
                                showHeader={false}
                            />}

                            {/* All action buttons in one row at the bottom */}
                            <div class={`d-flex flex-row flex-wrap justify-content-center mt-2 px-3 ${classNames}`}>
                                {!saleIsLocked && <button
                                    class={`btn btn-sm rounded-pill mx-1 py-0 ${newSale.selectedAnimal === animal.id ? 'btn-info' : 'btn-outline-info'}`}
                                    onClick={this.onClickSetSelectedAnimal}
                                    data-animal={animal.id}
                                    title={newSale.selectedAnimal === animal.id ? 'Aktiv – klikk for å fjerne' : 'Velg som aktivt dyr for nye produktlinjer'}
                                >
                                    <i class='fa-duotone fa-paw' /> <Text id='dyrejournal.animal.set-selected'>Select</Text>
                                </button>}
                                {showNewJournal && <button class='btn btn-sm btn-outline-primary rounded-pill mx-1 py-0' onClick={this.onClickNewJournal} data-animal={animal.id}>
                                    <i class='fa-solid fa-pen-nib' /> <Text id='dyrejournal.journal.new'>New journal</Text>
                                </button>}
                                {false && showNewJournal && animalJournals.length === 0 && <button class='btn btn-sm btn-outline-primary rounded-pill mx-1 py-0' onClick={this.onClickNewPrescription} data-animal={animal.id}>
                                    <i class='fa-solid fa-prescription' /> <Text id='dyrejournal.prescription.new'>New prescription</Text>
                                </button>}
                                {showNewBooking && <button class='btn btn-sm btn-outline-success rounded-pill mx-1 py-0' onClick={this.onClickNewBooking} data-animal={animal.id}>
                                    <i class='fa-duotone fa-calendar' /> <Text id='dyrejournal.booking.new'>New booking</Text>
                                </button>}
                                {showNewNotification && <button class='btn btn-sm btn-outline-warning rounded-pill mx-1 py-0' onClick={this.onClickNewNotification} data-animal={animal.id}>
                                    <i class='fa-solid fa-bell' /> <Text id='dyrejournal.notification.new'>New notification</Text>
                                </button>}
                                {showAdmission && <>
                                    {animal.isAdmittedToClinic ? <>
                                        <button class='btn btn-sm btn-outline-success rounded-pill mx-1 py-0' onClick={this.onClickNewAdmission} data-id={animal.id}>
                                            <i class='fa-solid fa-bed' /> <Text id='dyrejournal.leave-clinic'>Leave clinic</Text>
                                        </button>
                                    </> : <>
                                        <button class='btn btn-sm btn-outline-danger rounded-pill mx-1 py-0' onClick={this.onClickNewAdmission} data-id={animal.id}>
                                            <i class='fa-solid fa-bed' /> <Text id='dyrejournal.admission.new'>Admission</Text>
                                        </button>
                                    </>}
                                </>}
                                <button class='btn btn-sm btn-outline-secondary rounded-pill mx-1 py-0' onClick={this.onClickViewJournals} data-animal={animal.id}>
                                    <i class='fa-solid fa-eye' /> <Text id='dyrejournal.journal.view-all'>Journals</Text>
                                </button>
                                <button class='btn btn-sm btn-outline-secondary rounded-pill mx-1 py-0' onClick={this.onClickViewPrescriptions} data-animal={animal.id}>
                                    <i class='fa-solid fa-prescription' /> <Text id='dyrejournal.prescription.view-all'>Prescriptions</Text>
                                </button>
                                {customer?.featureFlags?.idexx === 1 && <button class='btn btn-sm btn-outline-secondary rounded-pill mx-1 py-0' onClick={this.onClickViewIdexxResults} data-animal={animal.id}>
                                    <i class='fa-solid fa-vial' /> <Text id='dyrejournal.idexx.view-all'>IDEXX</Text>
                                </button>}
                                <button class='btn btn-sm btn-outline-secondary rounded-pill mx-1 py-0' onClick={this.onClickFindFiles} data-animal={animal.id}>
                                    <i class='fa-solid fa-files' /> <Text id='dyrejournal.find-files'>Files</Text>
                                </button>
                                <button class='btn btn-sm btn-outline-secondary rounded-pill mx-1 py-0' onClick={this.onClickAiSummary} data-animal={animal.id}>
                                    <i class='fa-solid fa-wand-magic-sparkles' />
                                </button>
                            </div>
                        </div>
                    </>);
                })}
            </>}

            {!(finalAnimals && finalAnimals.length > 0) && <>
                <div class='d-flex flex-column justify-content-center w-100 my-1 text-muted'>
                    <div class='w-100 text-center' style='font-size: 1.5em;'>
                        <i class='fa-solid fa-dog' />
                    </div>
                    <div class='w-100 text-center' style='font-size: 1.0em;'>
                        <Text id='dyrejournal.animal.no-animal-selected'>No animals selected</Text>
                    </div>
                </div>
            </>}

            {showNewJournalConfirm && (
                <div
                    class='modal d-block'
                    tabindex='-1'
                    style='background-color: rgba(0,0,0,0.6); z-index: 1060;'
                    onClick={this.cancelNewJournal}
                >
                    <div class='modal-dialog modal-dialog-centered' onClick={e => e.stopPropagation()}>
                        <div class='modal-content border border-4 border-primary shadow-lg' style='border-radius: 0;'>
                            <div class='modal-header bg-primary text-white' style='border-radius: 0;'>
                                <h4 class='modal-title mb-0'>
                                    <i class='fa-solid fa-pen-nib me-2' />
                                    <Text id='dyrejournal.new-journal-confirm-title'>Create new journal?</Text>
                                </h4>
                                <button type='button' class='btn-close btn-close-white' aria-label='Close' onClick={this.cancelNewJournal} />
                            </div>
                            <div class='modal-body py-4'>
                                <p class='fs-5 mb-0'>{newJournalAreYouSure}</p>
                            </div>
                            <div class='modal-footer'>
                                <button type='button' class='btn btn-lg btn-outline-secondary rounded-pill px-4' onClick={this.cancelNewJournal}>
                                    <Text id='dyrejournal.new-journal-confirm-cancel'>Cancel</Text>
                                </button>
                                <button type='button' class='btn btn-lg btn-primary rounded-pill px-4' onClick={this.confirmNewJournal}>
                                    <i class='fa-solid fa-pen-nib me-2' />
                                    <Text id='dyrejournal.new-journal-confirm-confirm'>Create journal</Text>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>);
    }
}

export default AnimalListMini;

